import gql from 'graphql-tag';
import { compose } from 'ramda';

import { selectParams } from '#src/store/location/selectors';
import { setInvite } from '#src/store/content/actions';
import { setIsQaTeam } from '#src/store/session/actions';
import { toNotFound } from '#src/store/location/actions';
import { logger } from '#src/lib/logger';
import { baseImageGraphqlFields } from '#src/lib/wb-asset/image-graphql-fields';

const mediaFields = `
  ... on Cloudinary {
    ${baseImageGraphqlFields}
    resourceType
  }
`;

// The token is the host's page name (their falkor SuperchargerHostInvite,
// e.g. "robin"), not a game's invite - the page belongs to the host. The
// host's settings (video, copy, signup count) come from
// user.superchargerHost, and what the page shows right now (open, coming
// up, in progress, nothing planned) from its textClubState.
export const query = gql`
  query TextClubInvite($token: String) {
    invite(token: $token) {
      id
      token
      userId
      user {
        id
        firstName
        bio
        avatarV2 {
          value
          type
        }
        superchargerHost {
          hostUserId
          signupCount
          headline
          subHeadline
          pronoun
          displayName
          heroMedia {
            ${mediaFields}
          }
          textClubState
        }
      }
    }
  }
`;

// The games textClubState points to, for what it only has ids for: the
// calendar game's name, price, length, calendar, finish line, prizes and
// landing page copy, and the running game's length (its "day 12 of 28").
export const gamesQuery = gql`
  query TextClubGames(
    $calendarGameId: Int!
    $runningGameId: Int!
    $hasCalendarGame: Boolean!
    $hasRunningGame: Boolean!
  ) {
    calendarGame: game(id: $calendarGameId, type: falkor)
      @include(if: $hasCalendarGame) {
      ... on FalkorGame {
        id
        title
        startDate
        betAmount
        requiredMetricCount
        prizes
        freeSpot
        landingPage {
          headline
          sponsorName
          countries
          refundDeadline
          extraPrizes
          joiningPerk
          photoVideoPerk
        }
        subroundDetails {
          numSubroundsPerRound
        }
        superchargerMessaging {
          notifications
        }
      }
    }
    runningGame: game(id: $runningGameId, type: falkor)
      @include(if: $hasRunningGame) {
      ... on FalkorGame {
        id
        subroundDetails {
          numSubroundsPerRound
        }
      }
    }
  }
`;

// Best effort: without them the page still works, minus the calendar and
// the price.
const fetchGames = async (graphqlClient, textClubState) => {
  const calendarGameId = textClubState?.calendarGameId;
  const runningGameId = textClubState?.runningGame?.gameId;
  if (!calendarGameId && !runningGameId) return {};

  try {
    const { data } = await graphqlClient.query({
      query: gamesQuery,
      variables: {
        calendarGameId: calendarGameId || 0,
        runningGameId: runningGameId || 0,
        hasCalendarGame: !!calendarGameId,
        hasRunningGame: !!runningGameId,
      },
    });
    return {
      calendarGame: data?.calendarGame || null,
      runningGame: data?.runningGame || null,
    };
  } catch (error) {
    logger.error(error);
    return {};
  }
};

export const path = '/text-club/:inviteToken';
export const component = 'TextClub';
export const thunk =
  ({ graphqlClient }) =>
  async (dispatch, getState) => {
    const { inviteToken, is_qa_team: isQaTeam } = selectParams(getState());
    if (isQaTeam === 'true') dispatch(setIsQaTeam(true));

    const { data } = await graphqlClient.query({
      query,
      fetchPolicy: 'network-only',
      variables: { token: inviteToken },
    });

    // Any other invite (a game's, or a user who isn't a host) has no page.
    const { invite } = data || {};
    const superchargerHost = invite?.user?.superchargerHost;
    if (!superchargerHost) {
      dispatch(toNotFound());
      return;
    }

    const games = await fetchGames(
      graphqlClient,
      superchargerHost.textClubState
    );
    compose(
      dispatch,
      setInvite
    )({
      ...invite,
      user: {
        ...invite.user,
        superchargerHost: { ...superchargerHost, ...games },
      },
    });
  };
